import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Trash2, XCircle } from "lucide-react";
import { dealSubmissionApi } from "../api/dealSubmission";
import type {
  BrokerDashboardChipGroup,
  PortalDealSubmissionListDto,
  PortalDealSubmissionListItemDto,
} from "../api/types";
import { Badge, type BadgeProps } from "@/design-system/components/ui/Badge";
import { Button, buttonVariants } from "@/design-system/components/ui/Button";
import { useSubmittingMutation } from "@/design-system/components/ui/useSubmittingMutation";
import { Modal } from "@/components/ui/Modal";
import { RowActions } from "@/components/ux/RowActions";
import { cn } from "@/design-system/lib/utils";
// Dates go through the PURE formatters with an EXPLICIT null zone, never the
// staff date-formatter hook in `@/utils/datetime` — that hook reads the staff
// AuthContext, which this tree never mounts. See portal/isolation.test.ts
// (FORBIDDEN_PREFIXES) and portal/portalInstantsRenderInOneZone.test.tsx.
import { formatDateInTz, formatDateTimeInTz, formatRelativeTime } from "@/design-system/lib/format";
import { usePortalAuth } from "../PortalAuthContext";
import { BrokerLockedNotice } from "../components/BrokerLockedNotice";

// Broker deals list — canonical surface. Rendered as the broker
// dashboard's "Deals & status" tab (/portal/broker?tab=deals). The
// former standalone "/portal/broker/deals" ("My deals") screen was a
// duplicate that reused this same component; it now redirects here, so
// this is the single deals list — do not reintroduce a second one.

interface FilterChip {
  key: string;
  label: string;
  group: BrokerDashboardChipGroup;
}

const FILTER_CHIPS: ReadonlyArray<FilterChip> = [
  { key: "all", label: "All", group: "All" },
  { key: "drafts", label: "Drafts", group: "Drafts" },
  { key: "submitted", label: "Submitted", group: "Submitted" },
  { key: "in-staging", label: "In Staging", group: "InStaging" },
  { key: "returned", label: "Returned", group: "Returned" },
  { key: "active", label: "Active", group: "Active" },
  { key: "closed", label: "Closed", group: "Closed" },
  { key: "declined-withdrawn", label: "Declined/Withdrawn", group: "DeclinedOrWithdrawn" },
];

const PAGE_SIZE = 25;

type SortKey = "recent" | "status" | "name";

const SORT_OPTIONS: ReadonlyArray<{ key: SortKey; label: string }> = [
  { key: "recent", label: "Recently updated" },
  { key: "status", label: "Status" },
  { key: "name", label: "Name" },
];

// Status → badge taxonomy, aligned with the filter chips. Each state gets
// a distinct variant + text label so two same-named rows (e.g. the paired
// "Desert Ridge Specialty Plumbing" entries) are distinguishable at a
// glance — and the label keeps it from being colour-alone.
function statusBadge(
  row: PortalDealSubmissionListItemDto,
): { label: string; status: NonNullable<BadgeProps["status"]> } {
  switch (row.status) {
    case "Draft":
      return { label: "Draft", status: "draft" };
    case "Submitted":
      return { label: "Submitted", status: "info" };
    case "InStaging":
      return { label: "In Staging", status: "review" };
    case "ReturnedForChanges":
      return { label: "Returned", status: "error" };
    case "Approved":
      return { label: "Active", status: "finalized" };
    case "Declined":
      return { label: "Declined", status: "archived" };
    case "Withdrawn":
      return { label: "Withdrawn", status: "archived" };
    // A Deal assigned to the broker with no backing submission reads exactly
    // like an imported deal — its stage is the linked Deal.Stage.
    case "NoSubmission":
    case "Imported":
      if (row.stage === "Closed") return { label: "Closed", status: "gold" };
      if (row.stage === "Declined") return { label: "Declined", status: "archived" };
      if (row.stage === "Withdrawn") return { label: "Withdrawn", status: "archived" };
      return { label: "Active", status: "finalized" };
    default:
      return { label: row.stageLabel, status: "info" };
  }
}

// Client-side replica of the server's chip filter (PortalDealSubmission
// StatusService.ApplyChipFilter). The linked-deal pipeline stage the
// server queries is already encoded in `row.stage`, so each loaded item
// can be bucketed without a backend call. Returns null for states that
// only appear under "All" (e.g. Approved).
// Exported so the broker Home dashboard's pipeline mini-view buckets deals
// identically (single source of the server-mirrored chip logic — no drift).
export function chipGroupForRow(
  row: PortalDealSubmissionListItemDto,
): BrokerDashboardChipGroup | null {
  // Action needed (unaddressed reviewer comments) buckets under "Returned"
  // regardless of the underlying status — a per-item return leaves the
  // submission in Submitted/InStaging, so status alone would hide it. Mirrors
  // the server's ApplyChipFilter so client chip counts stay consistent.
  if (
    row.openReturnCommentCount > 0 &&
    (row.status === "Submitted" ||
      row.status === "InStaging" ||
      row.status === "ReturnedForChanges")
  ) {
    return "Returned";
  }
  switch (row.status) {
    case "Draft":
      return "Drafts";
    case "Submitted":
      return "Submitted";
    case "InStaging":
      return "InStaging";
    case "ReturnedForChanges":
      return "Returned";
    case "Declined":
    case "Withdrawn":
      return "DeclinedOrWithdrawn";
    // Deal-only rows bucket by their linked Deal.Stage exactly like an
    // imported deal (server: DealOnlyMatchesChip mirrors this).
    case "NoSubmission":
    case "Imported":
      if (row.stage === "Closed") return "Closed";
      if (row.stage === "Declined" || row.stage === "Withdrawn")
        return "DeclinedOrWithdrawn";
      if (
        row.stage === "Intake" ||
        row.stage === "PreApproval" ||
        row.stage === "Underwriting" ||
        row.stage === "Closing"
      )
        return "Active";
      return null;
    default:
      return null;
  }
}

const STATUS_SORT_ORDER: Record<PortalDealSubmissionListItemDto["status"], number> = {
  ReturnedForChanges: 0,
  Draft: 1,
  Submitted: 2,
  InStaging: 3,
  Approved: 4,
  Imported: 5,
  // Deal-only rows sort alongside imported deals (both are active pipeline
  // deals); duplicate ranks are fine for a stable sort.
  NoSubmission: 5,
  Declined: 6,
  Withdrawn: 7,
};

function sortName(row: PortalDealSubmissionListItemDto): string {
  return (row.businessName || row.title?.trim() || "￿").toLowerCase();
}

// The row's natural destination: a draft resumes the wizard, everything
// else drills into the read-only status view.
function destinationFor(row: PortalDealSubmissionListItemDto): string {
  if (row.status === "Draft" || row.status === "ReturnedForChanges")
    return `/portal/broker/deals/submissions/${row.id}`;
  return `/portal/broker/deals/submissions/${row.id}/status`;
}

export function BrokerDealsDashboard() {
  const auth = usePortalAuth();
  // fix-broker-portal — an uncertified broker can neither view nor create
  // deals. The deal endpoints would 403 ("Broker session required.") on a
  // freshly self-healed session, so short-circuit to the friendly
  // onboarding notice instead of fetching.
  const brokerLocked = auth.me?.kind === "Broker" && auth.me?.isCertified !== true;

  const [chip, setChip] = useState<string>("all");
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [sort, setSort] = useState<SortKey>("recent");
  const [page, setPage] = useState(1);
  const [data, setData] = useState<PortalDealSubmissionListDto | null>(null);
  // Snapshot of the full (single-page) "All" result, used to label the
  // filter chips with live counts. Null when the list is paginated, in
  // which case counts are omitted rather than shown wrong.
  const [allItems, setAllItems] = useState<PortalDealSubmissionListItemDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  // Bumped after a successful withdraw / delete to refetch the list.
  const [reloadKey, setReloadKey] = useState(0);

  const selectedChip = useMemo(
    () => FILTER_CHIPS.find((c) => c.key === chip) ?? FILTER_CHIPS[0],
    [chip],
  );

  // Live search — debounce keystrokes into the server query so the list
  // filters as you type (Enter still applies immediately via onSubmit).
  useEffect(() => {
    const handle = setTimeout(() => {
      setSearchTerm(searchInput.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(handle);
  }, [searchInput]);

  useEffect(() => {
    if (auth.loading) return; // wait for /me before deciding to fetch
    if (brokerLocked) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    void (async () => {
      try {
        const res = await dealSubmissionApi.dashboard({
          group: selectedChip.group,
          q: searchTerm || undefined,
          page,
          pageSize: PAGE_SIZE,
        });
        if (cancelled) return;
        setData(res);
        // Capture a complete, unfiltered snapshot for chip counts.
        if (
          selectedChip.group === "All" &&
          !searchTerm &&
          res.totalCount <= res.items.length
        ) {
          setAllItems(res.items);
        }
      } catch (err) {
        if (!cancelled)
          setError(err instanceof Error ? err.message : "Couldn't load your deals.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedChip, searchTerm, page, reloadKey, auth.loading, brokerLocked]);

  const counts = useMemo(() => {
    if (!allItems) return null;
    const c: Record<BrokerDashboardChipGroup, number> = {
      All: allItems.length,
      Drafts: 0,
      Submitted: 0,
      InStaging: 0,
      Returned: 0,
      Active: 0,
      Closed: 0,
      DeclinedOrWithdrawn: 0,
    };
    for (const it of allItems) {
      const g = chipGroupForRow(it);
      if (g) c[g] += 1;
    }
    return c;
  }, [allItems]);

  const sortedItems = useMemo(() => {
    if (!data) return [];
    const items = [...data.items];
    // Action-needed rows (unaddressed reviewer comments) are always pinned to
    // the top so a returned deal can't sit unnoticed below quiet in-review
    // ones. Within each group the chosen sort applies; Array.sort is stable,
    // so "recent" preserves the server order (ModifiedAt desc).
    const actionRank = (r: PortalDealSubmissionListItemDto) =>
      r.openReturnCommentCount > 0 ? 0 : 1;
    items.sort((a, b) => {
      const byAction = actionRank(a) - actionRank(b);
      if (byAction !== 0) return byAction;
      if (sort === "name") return sortName(a).localeCompare(sortName(b));
      if (sort === "status")
        return STATUS_SORT_ORDER[a.status] - STATUS_SORT_ORDER[b.status];
      return 0;
    });
    return items;
  }, [data, sort]);

  function onSearchSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSearchTerm(searchInput.trim());
    setPage(1);
  }

  function onChipClick(key: string) {
    setChip(key);
    setPage(1);
  }

  if (brokerLocked) {
    return (
      <BrokerLockedNotice
        title="Deals & status"
        description="Submitting deals and tracking their status will be available here once you're certified."
      />
    );
  }

  return (
    <section aria-label="Your deals">
      <div className="flex items-center justify-end">
        <Link
          to="/portal/broker/deals/new"
          className={cn(buttonVariants({ variant: "primary", size: "md" }), "no-underline")}
          data-testid="new-deal-link"
        >
          Submit a new deal
        </Link>
      </div>

      <div className="mt-space-3 flex flex-wrap items-center justify-between gap-space-3">
        <form onSubmit={onSearchSubmit} className="flex items-center gap-space-2 w-full sm:w-auto">
          <input
            type="search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search by deal name…"
            aria-label="Search your deals"
            className="text-small border border-border-light rounded-md px-space-2 py-space-1 w-full sm:min-w-[18rem]"
            data-testid="deal-dashboard-search"
          />
        </form>

        <label className="flex items-center gap-space-2 text-caption text-gray-mid">
          Sort
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value as SortKey)}
            aria-label="Sort deals"
            data-testid="deal-dashboard-sort"
            className="text-small text-onyx border border-border-light rounded-md px-space-2 py-space-1 bg-white-pure"
          >
            {SORT_OPTIONS.map((o) => (
              <option key={o.key} value={o.key}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div
        className="mt-space-3 flex flex-wrap items-center gap-space-2"
        role="tablist"
        aria-label="Status filters"
      >
        {FILTER_CHIPS.map((c) => {
          const count = counts ? counts[c.group] : null;
          return (
            <button
              key={c.key}
              type="button"
              role="tab"
              aria-selected={chip === c.key}
              onClick={() => onChipClick(c.key)}
              data-testid={`deal-dashboard-chip-${c.key}`}
              className={
                "text-caption px-space-2 py-space-1 rounded-full border " +
                (chip === c.key
                  ? "border-onyx bg-onyx text-white-pure"
                  : "border-border-light text-onyx bg-white-pure")
              }
            >
              {c.label}
              {count !== null && (
                <span className="ml-space-1 opacity-70" data-testid={`deal-dashboard-count-${c.key}`}>
                  · {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {error && (
        <p className="mt-space-4 text-small text-status-error" role="alert">
          {error}
        </p>
      )}

      {loading && !error && (
        <p className="mt-space-4 text-small text-gray-mid">Loading…</p>
      )}

      {!loading && !error && data && (
        <DashboardList
          items={sortedItems}
          data={data}
          page={page}
          setPage={setPage}
          chipKey={chip}
          onChanged={() => setReloadKey((k) => k + 1)}
        />
      )}
    </section>
  );
}

function DashboardList({
  items,
  data,
  page,
  setPage,
  chipKey,
  onChanged,
}: {
  items: PortalDealSubmissionListItemDto[];
  data: PortalDealSubmissionListDto;
  page: number;
  setPage: (n: number) => void;
  chipKey: string;
  onChanged: () => void;
}) {
  const totalPages = Math.max(1, Math.ceil(data.totalCount / Math.max(1, data.pageSize)));

  if (items.length === 0) {
    return (
      <p className="mt-space-4 text-small text-gray-mid" data-testid="deal-dashboard-empty">
        {chipKey === "all"
          ? "You haven't started any deal submissions yet."
          : "No submissions match this filter."}
      </p>
    );
  }

  return (
    <>
      <ul className="mt-space-4 flex flex-col gap-space-3 list-none p-0">
        {items.map((row) => (
          <DealRow key={row.id} row={row} onChanged={onChanged} />
        ))}
      </ul>

      {totalPages > 1 && (
        <nav
          className="mt-space-4 flex items-center gap-space-3"
          aria-label="Pagination"
        >
          <button
            type="button"
            onClick={() => setPage(Math.max(1, page - 1))}
            disabled={page <= 1}
            className="text-small text-onyx underline disabled:text-gray-mid disabled:no-underline"
          >
            Previous
          </button>
          <span className="text-caption text-gray-mid">
            Page {page} of {totalPages}
          </span>
          <button
            type="button"
            onClick={() => setPage(Math.min(totalPages, page + 1))}
            disabled={page >= totalPages}
            className="text-small text-onyx underline disabled:text-gray-mid disabled:no-underline"
          >
            Next
          </button>
        </nav>
      )}
    </>
  );
}

// A broker may withdraw any of their own submissions that haven't been
// imported/approved yet (terminal statuses can't be undone). For drafts
// the same action is framed as a delete — you don't "withdraw" something
// that was never submitted.
const WITHDRAWABLE_STATUSES: ReadonlyArray<PortalDealSubmissionListItemDto["status"]> = [
  "Draft",
  "Submitted",
  "InStaging",
  "ReturnedForChanges",
];

function DealRow({
  row,
  onChanged,
}: {
  row: PortalDealSubmissionListItemDto;
  onChanged: () => void;
}) {
  const auth = usePortalAuth();
  // Broker money core (WS3) — a Junior broker controls whether each of their
  // stay-direct / imported deals is shared with their covering brokerage.
  // Shared by default; only an explicit `false` means the junior hid it.
  const isJunior = auth.me?.brokerType === "Junior";
  const isJuniorDeal = isJunior && !!row.dealId;
  // WS6 — the interactive Shared/Private toggle is only meaningful for a
  // stay-direct deal. For a firm-routed deal (`brokerPayeeStayDirect === false`
  // — absorbed conversions AND junior deals that close firm-routed) the
  // server-side firm-visibility predicate is `!brokerPayeeStayDirect ||
  // sharedWithBrokerageByBroker`, so the share flag is irrelevant and clicking
  // "Private" is a genuine no-op. Hide the control (and render a static "Shared
  // with brokerage" indicator instead) so the old false "now hidden" toast is
  // structurally unreachable. Gate on the EXACT column the predicate keys on.
  const showVisibility = isJuniorDeal && row.brokerPayeeStayDirect === true;
  const showSharedIndicator = isJuniorDeal && !showVisibility;
  const sharedWithBrokerage = row.sharedWithBrokerageByBroker !== false;
  const isDraft = row.status === "Draft";
  // A deal-only row (broker-assigned Deal with no backing submission) has no
  // submission drill-in — its id is a Deal id, so a /submissions/{id}/status
  // link would 404. Render it non-navigable.
  const hasSubmission = row.status !== "NoSubmission";
  const named = row.businessName || row.title?.trim();
  const displayName = named
    ? named
    : isDraft
      ? `Draft started ${formatDateInTz(row.createdAt, null, { year: undefined })}`
      : "Untitled submission";
  const dealNumberLabel = row.dealNumber ? `· #${row.dealNumber}` : "";
  const badge = statusBadge(row);
  const destination = destinationFor(row);
  const affordanceLabel = isDraft
    ? "Continue"
    : row.status === "ReturnedForChanges"
      ? "Resume"
      : "View status";

  const [confirming, setConfirming] = useState(false);
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [withdrawError, setWithdrawError] = useState<string | null>(null);
  const canWithdraw = WITHDRAWABLE_STATUSES.includes(row.status);

  async function doWithdraw() {
    setSubmitting(true);
    setWithdrawError(null);
    try {
      await dealSubmissionApi.withdraw(row.id, reason.trim() || undefined);
      setConfirming(false);
      setReason("");
      onChanged();
    } catch (err) {
      setWithdrawError(
        err instanceof Error ? err.message : "Couldn't complete that.",
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <li
      data-testid={`deal-dashboard-row-${row.id}`}
      className="relative p-space-3 border border-border-light rounded-md bg-white-pure hover:border-gold focus-within:border-gold transition-colors"
    >
      <div className="flex items-start justify-between gap-space-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-space-2">
            <Badge status={badge.status} data-testid={`deal-dashboard-badge-${row.id}`}>
              {badge.label}
            </Badge>
            {row.openReturnCommentCount > 0 && (
              <Badge status="error" data-testid={`deal-dashboard-action-needed-${row.id}`}>
                <AlertTriangle size={11} className="mr-1 inline" aria-hidden />
                Action needed · {row.openReturnCommentCount} comment
                {row.openReturnCommentCount === 1 ? "" : "s"}
              </Badge>
            )}
          </div>
          {/* Stretched link — the name is a real focusable link whose
              ::after overlay makes the whole card clickable. The actions
              column below sits at z-10 so it stays independently usable. A
              deal-only row has no drill-in, so its name is plain text. */}
          <p className="text-small font-medium text-onyx m-0 mt-space-1 truncate">
            {hasSubmission ? (
              <Link
                to={destination}
                data-testid={`deal-dashboard-open-${row.id}`}
                className="text-onyx no-underline hover:underline after:absolute after:inset-0 after:content-[''] focus-visible:outline-none focus-visible:underline"
              >
                {displayName}
              </Link>
            ) : (
              <span
                data-testid={`deal-dashboard-name-${row.id}`}
                className="text-onyx"
              >
                {displayName}
              </span>
            )}{" "}
            {dealNumberLabel}
          </p>
          <p className="text-caption text-gray-mid m-0 mt-space-1">
            <span data-testid={`deal-dashboard-stage-${row.id}`}>
              {row.stageLabel}
            </span>
            {" · "}
            <span title={formatDateTimeInTz(row.lastUpdatedAt, null, { second: "2-digit" })}>
              Updated {formatRelativeTime(row.lastUpdatedAt, null)}
            </span>
            {row.liaisonName && (
              <>
                {" · "}
                <span>Liaison: {row.liaisonName}</span>
              </>
            )}
          </p>
          {row.openReturnCommentCount > 0 && (
            <p
              className="text-caption font-semibold text-status-warning m-0 mt-space-1"
              data-testid={`deal-dashboard-return-comments-${row.id}`}
            >
              {row.openReturnCommentCount} liaison comment
              {row.openReturnCommentCount === 1 ? "" : "s"} to review ·{" "}
              <Link
                to={`/portal/broker/deals/submissions/${row.id}/status`}
                className="relative z-10 text-status-warning underline"
                data-testid={`deal-dashboard-view-comments-${row.id}`}
              >
                View comments
              </Link>
            </p>
          )}
          {row.outstandingCount > 0 && (
            <p
              className="text-caption text-status-warning m-0 mt-space-1"
              data-testid={`deal-dashboard-outstanding-${row.id}`}
            >
              {isDraft
                ? `${row.outstandingCount} item${row.outstandingCount === 1 ? "" : "s"} left to finish`
                : `${row.outstandingCount} outstanding item${row.outstandingCount === 1 ? "" : "s"}`}
            </p>
          )}
        </div>
        <div className="relative z-10 flex items-center gap-space-2 shrink-0">
          {showVisibility && row.dealId && (
            <JuniorVisibilityToggle
              dealId={row.dealId}
              shared={sharedWithBrokerage}
              onChanged={onChanged}
            />
          )}
          {showSharedIndicator && (
            // WS6 — firm-routed deal: the toggle would be inert, so show a
            // static, non-interactive indicator (no POST possible).
            <Badge
              status="archived"
              data-testid={`deal-visibility-shared-${row.id}`}
              aria-label="Shared with your brokerage"
            >
              Shared with brokerage
            </Badge>
          )}
          {hasSubmission && (
            <Link
              to={destination}
              className="text-small text-onyx underline"
              data-testid={
                isDraft
                  ? `deal-dashboard-continue-${row.id}`
                  : row.status === "ReturnedForChanges"
                    ? `deal-dashboard-resume-${row.id}`
                    : `deal-dashboard-view-${row.id}`
              }
            >
              {affordanceLabel}
            </Link>
          )}
          {canWithdraw && (
            <RowActions
              label={`Actions for ${displayName}`}
              actions={[
                {
                  key: "withdraw",
                  label: isDraft ? "Delete draft" : "Withdraw submission",
                  icon: isDraft ? Trash2 : XCircle,
                  destructive: true,
                  onClick: () => setConfirming(true),
                },
              ]}
            />
          )}
        </div>
      </div>

      <Modal
        open={confirming}
        onClose={() => {
          if (submitting) return;
          setConfirming(false);
          setReason("");
          setWithdrawError(null);
        }}
        size="sm"
        title={isDraft ? "Delete this draft?" : "Withdraw this submission?"}
        description={
          isDraft
            ? "This removes the draft from your list. This can't be undone."
            : "This withdraws the submission from review. You can add an optional reason for your records."
        }
        footer={
          <>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                setConfirming(false);
                setReason("");
                setWithdrawError(null);
              }}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={doWithdraw}
              loading={submitting}
              data-testid={`deal-dashboard-withdraw-confirm-${row.id}`}
            >
              {isDraft ? "Delete draft" : "Withdraw"}
            </Button>
          </>
        }
      >
        {!isDraft && (
          <textarea
            rows={3}
            className="w-full border border-border-light rounded-md p-space-2 text-small"
            placeholder="Optional reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            aria-label="Reason for withdrawing"
          />
        )}
        {withdrawError && (
          <p className="text-caption text-status-error m-0 mt-space-2" role="alert">
            {withdrawError}
          </p>
        )}
      </Modal>
    </li>
  );
}

// Broker money core (WS3) — a Junior broker's per-deal Shared/Private toggle.
// Shared by default; POSTs the new state and refetches the list via the
// caller's onChanged. Optimistic locally so the choice reflects immediately;
// reverts to server truth on failure.
function JuniorVisibilityToggle({
  dealId,
  shared,
  onChanged,
}: {
  dealId: string;
  shared: boolean;
  onChanged: () => void;
}) {
  const [current, setCurrent] = useState(shared);
  const save = useSubmittingMutation(
    (next: boolean) => dealSubmissionApi.setDealVisibility(dealId, next),
    {
      refresh: onChanged,
      onError: () => setCurrent(shared),
    },
  );

  function choose(next: boolean) {
    if (next === current || save.pending) return;
    setCurrent(next);
    void save.submit(next);
  }

  return (
    <span
      className="inline-flex items-center gap-space-1 rounded-md border border-border-light p-[2px]"
      role="radiogroup"
      aria-label="Share this deal with your brokerage"
      data-testid={`deal-visibility-${dealId}`}
    >
      {([
        { value: true, label: "Shared" },
        { value: false, label: "Private" },
      ] as const).map((o) => (
        <button
          key={o.label}
          type="button"
          role="radio"
          aria-checked={current === o.value}
          disabled={save.pending}
          onClick={() => choose(o.value)}
          className={
            "text-caption px-space-2 py-[2px] rounded " +
            (current === o.value
              ? "bg-onyx text-white-pure"
              : "text-gray-mid hover:text-onyx")
          }
        >
          {o.label}
        </button>
      ))}
    </span>
  );
}
